import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { admin, errorMessage } from "../lib/api";
import { date, TICKET_CATEGORY, TICKET_STATUS } from "../lib/format";
import type { List, SupportTicket, SupportTicketDetail } from "../lib/types";
import { Alert, Badge, Button, Field, Modal, PageHeader, Spinner, Table } from "../components/ui";

export default function Support() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "technical", body: "" });
  const [error, setError] = useState<string>();
  const query = useInfiniteQuery({
    queryKey: ["support", status],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: "50" });
      if (status) p.set("status", status);
      if (pageParam) p.set("cursor", pageParam);
      return admin.get<List<SupportTicket>>(`/support/tickets?${p}`);
    },
    getNextPageParam: (l) => l.next_cursor || undefined,
    refetchInterval: 30_000,
  });
  const create = useMutation({
    mutationFn: () => admin.post<SupportTicketDetail>("/support/tickets", form),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["support"] });
      setOpen(false);
      setForm({ subject: "", category: "technical", body: "" });
      nav(`/support/${t.id}`);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const rows = query.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <PageHeader
        title="Suport"
        subtitle="Scrie-ne când ai nevoie de ajutor. Îți răspundem aici, de obicei în aceeași zi lucrătoare."
        actions={<Button onClick={() => { setError(undefined); setOpen(true); }}>Tichet nou</Button>}
      />
      <div className="mb-4">
        <select className="input w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filtru status">
          <option value="active">Active</option>
          <option value="resolved">Rezolvate</option>
          <option value="closed">Închise</option>
          <option value="">Toate</option>
        </select>
      </div>
      {query.isLoading ? <Spinner /> : (
        <Table head={["Tichet", "Status", "Ultimul mesaj", "Deschis"]} empty={rows.length === 0}>
          {rows.map((t) => {
            const s = TICKET_STATUS[t.status];
            return (
              <tr key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/support/${t.id}`)}>
                <td className="px-4 py-3">
                  <div className={t.status === "waiting_merchant" ? "font-bold" : "font-semibold"}><span className="text-slate-400">#{t.number}</span> {t.subject}</div>
                  <div className="text-xs text-slate-400">{TICKET_CATEGORY[t.category] ?? t.category}</div>
                </td>
                <td className="whitespace-nowrap px-4 py-3"><Badge tone={s?.tone}>{s?.label ?? t.status}</Badge></td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">{date(t.last_message_at)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">{date(t.created_at)}</td>
              </tr>
            );
          })}
        </Table>
      )}
      {query.hasNextPage && <div className="mt-4 text-center"><Button variant="secondary" onClick={() => query.fetchNextPage()}>Încarcă mai multe</Button></div>}

      <Modal open={open} onClose={() => setOpen(false)} title="Tichet nou">
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Field label="Subiect">
            <input className="input" required minLength={3} maxLength={200} autoFocus value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </Field>
          <Field label="Categorie">
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {Object.entries(TICKET_CATEGORY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Descriere" hint="Ce ai încercat să faci, ce s-a întâmplat și, dacă e cazul, numărul comenzii.">
            <textarea className="input min-h-32" required maxLength={10000} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </Field>
          {error && <Alert>{error}</Alert>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Anulează</Button>
            <Button disabled={create.isPending || form.subject.trim().length < 3 || !form.body.trim()}>Trimite</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
